<template>
  <div class="article-detail-view page">
    <div
      class="page__inner"
    >
      <AppPageLoader :collection="pageCollection" @loaded="onPageLoaded">
        <AppArticleHero
          v-if="state.data"
          :image="state.data.image"
          :primary-tag="state.data.belongs_to[0]?.item ? state.data.belongs_to[0].item.title : undefined"
          class="article-detail-view__article-hero"
        />
        <article
          v-if="state.data"
          class="article-detail-view__article"
        >
          <AppLayoutContainer :flush="true">
            <component :is="state.update ? 'span' : 'time'" v-if="state.date" :datetime="!state.update ? state.data.date_created : null" class="article-detail-view__component">
              {{ state.date }}
            </component>
            <time v-if="state.update" :datetime="state.data.date_revisioned" class="article-detail-view__time">
              {{ state.update }}
            </time>
            <h1
              class="article-detail-view__title"
              v-html="state.data.title"
            />
            <AppRichText v-if="state.data.introduction" :data="state.data.introduction" class="article-detail-view__rich-text app-rich-text--introduction" />
            <AppTagButtonList :data="state.data.belongs_to" class="article-detail-view__tag-button-list" />
          </AppLayoutContainer>
          <AppDynamicBlock
            :is="useBlock(block)"
            v-for="block in state.data.content"
            :key="`block_${block.id}`"
            :data="block"
          />
          <ClientOnly>
            <LayoutsShare v-if="state.data?.share" />
          </ClientOnly>
          <AppSterBanner />
          <AppGridBlockStatic v-if="state.data" variant="more" />
          <AppGridBlockStatic v-if="state.data" variant="other" />
        </article>
      </AppPageLoader>
    </div>
    <AppAnimatedTitle />
    <LayoutsFooter class="article-detail-view__footer" />
  </div>
</template>

<script setup lang="ts">
import { TArticlePage } from '~/types/page-types'
import { useBlock } from '#imports'
import formatDate from '~/utils/date'

const pageCollection = 'article_page'

type TState = {
  data?: TArticlePage
  date?: string
  update?: string
};

const state = reactive<TState>({
  data: undefined,
  date: undefined,
  update: undefined
})

const onPageLoaded = (data) => {
  state.data = data
  state.date = formatDate(data.date_created)

  if (data.date_revisioned !== null && data.date_revisioned !== data.date_created) {
    state.update = `Update: ${formatDate(data.date_revisioned)}`
  }

  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0)
  }

  const publishedDate = data?.date_created ? new Date(data?.date_created).toISOString().split('T')[0] : undefined
  const revisionDate = data?.date_revisioned ? new Date(data?.date_revisioned).toISOString().split('T')[0] : undefined

  useJsonld({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.title,
    datePublished: publishedDate,
    dateModified: revisionDate,
    author: {
      '@type': 'Organization',
      name: 'AVROTROS'
    }
  })
}

onMounted(() => {
  window.scrollTo(0, 0)
})

</script>

<style scoped lang="postcss">
.article-detail-view {
  display: flex;
  min-height: 100dvh;
  flex-direction: column;
  background-color: var(--color-blue-prussian);

  &__article-hero {
    z-index: 10;
    @mixin app-show-hero;
  }

  &__article {
    position: relative;
    z-index: 20;
    background-color: var(--color-blue-prussian);
    padding-top: 1.5rem;
    @mixin app-show-content;
    /* fix the bottom of the article page. */
    padding-bottom: 2.5rem;

    @media (--lg) {
      padding-top: 2.5rem;
      padding-bottom: 3.5rem;
    }

    @media (--xl) {
      padding-bottom: 5rem;
    }

    > :last-child {
      margin-bottom: 0px;
    }
  }

  &__component {
    margin-bottom: 0.5rem;
    display: block;
    font-family: Midnight, sans-serif;
    font-size: 0.75rem;
    text-transform: uppercase;
    line-height: 0.8;
    color: #fff;
  }

  &__time {
    margin-bottom: 0.5rem;
    display: block;
    font-family: Midnight, sans-serif;
    font-size: 0.75rem;
    text-transform: uppercase;
    line-height: 0.8;
    color: #fff;
  }

  &__title {
    margin-bottom: 2rem;
    font-family: GraphikWide, sans-serif;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.2;

    @media (--sm) {
      margin-bottom: 1rem;
    }

    @media (--lg) {
      font-size: 35px;
      line-height: 1.2;
    }
  }

  &__rich-text {
    margin-bottom: 1.5rem;
  }

  &__tag-button-list {
    @mixin app-block-margins;
  }

  &__footer {
    margin-top: auto;
  }
}
</style>
